import type { Locale } from './messages';

/**
 * Libellés des questions de cadrage et des identifiants d'entreprise, par langue. Séparés des
 * textes d'écran parce qu'ils décrivent des données (domaines, outils, registres) : leurs clés sont
 * celles des faits déclarés, jamais traduites. Les noms de produits (Odoo, Sellsy…) ne se traduisent pas.
 */

export type RegistrationKind = 'FR' | 'BE' | 'CH' | 'LU' | 'OTHER';

export interface DomainMessages {
  registration: Record<RegistrationKind, { label: string; help: string }>;
  questions: {
    target_domains: { label: string; help: string; options: Record<'sales' | 'purchasing' | 'inventory' | 'production' | 'quality' | 'traceability' | 'accounting' | 'integrations', string> };
    current_systems: { label: string; options: Record<'spreadsheet' | 'custom' | 'paper' | 'other', string> };
    industry: { label: string; options: Record<'food' | 'manufacturing' | 'distribution' | 'textile' | 'other', string> };
    site_count_band: { label: string; options: Record<'1' | '2-3' | '4+', string> };
    user_count_band: { label: string; options: Record<'1-5' | '6-20' | '21-50' | '50+', string> };
  };
}

const fr: DomainMessages = {
  registration: {
    FR: { label: 'SIREN ou SIRET', help: 'Facultatif. Il nous permet de retrouver vos établissements et votre activité déclarée.' },
    BE: { label: 'Numéro d’entreprise (BCE)', help: 'Facultatif. 10 chiffres, par exemple 0403.170.701.' },
    CH: { label: 'Numéro IDE', help: 'Facultatif. Format CHE-123.456.789.' },
    LU: { label: 'Numéro RCS', help: 'Facultatif. Par exemple B123456.' },
    OTHER: { label: 'Numéro d’immatriculation de l’entreprise', help: 'Facultatif. Numéro du registre du commerce ou de TVA, tel qu’il figure sur vos documents.' },
  },
  questions: {
    target_domains: {
      label: 'Que souhaitez-vous gérer avec AYUMIA ?',
      help: 'Plusieurs choix possibles. Nous affinerons pendant l’entretien.',
      options: { sales: 'Ventes', purchasing: 'Achats', inventory: 'Stocks', production: 'Production', quality: 'Qualité', traceability: 'Traçabilité', accounting: 'Facturation', integrations: 'E-commerce et intégrations' },
    },
    current_systems: { label: 'Quels outils utilisez-vous aujourd’hui ?', options: { spreadsheet: 'Tableurs (Excel…)', custom: 'Logiciel sur mesure', paper: 'Papier', other: 'Autre' } },
    industry: { label: 'Votre secteur', options: { food: 'Agroalimentaire', manufacturing: 'Industrie', distribution: 'Négoce et distribution', textile: 'Textile', other: 'Autre' } },
    site_count_band: { label: 'Combien de sites (ateliers, entrepôts, boutiques) ?', options: { '1': '1', '2-3': '2 à 3', '4+': '4 ou plus' } },
    user_count_band: { label: 'Combien de personnes utiliseraient AYUMIA ?', options: { '1-5': '1 à 5', '6-20': '6 à 20', '21-50': '21 à 50', '50+': 'Plus de 50' } },
  },
};

const en: DomainMessages = {
  registration: {
    FR: { label: 'SIREN or SIRET number', help: 'Optional. It lets us find your establishments and registered activity.' },
    BE: { label: 'Company number (CBE)', help: 'Optional. 10 digits, for example 0403.170.701.' },
    CH: { label: 'UID number', help: 'Optional. Format CHE-123.456.789.' },
    LU: { label: 'RCS number', help: 'Optional. For example B123456.' },
    OTHER: { label: 'Company registration number', help: 'Optional. Trade register or VAT number, as shown on your documents.' },
  },
  questions: {
    target_domains: {
      label: 'What would you like to manage with AYUMIA?',
      help: 'Several choices possible. We will refine this during the interview.',
      options: { sales: 'Sales', purchasing: 'Purchasing', inventory: 'Inventory', production: 'Production', quality: 'Quality', traceability: 'Traceability', accounting: 'Invoicing', integrations: 'E-commerce and integrations' },
    },
    current_systems: { label: 'Which tools do you use today?', options: { spreadsheet: 'Spreadsheets (Excel…)', custom: 'Custom software', paper: 'Paper', other: 'Other' } },
    industry: { label: 'Your industry', options: { food: 'Food', manufacturing: 'Manufacturing', distribution: 'Wholesale and distribution', textile: 'Textile', other: 'Other' } },
    site_count_band: { label: 'How many sites (workshops, warehouses, shops)?', options: { '1': '1', '2-3': '2 to 3', '4+': '4 or more' } },
    user_count_band: { label: 'How many people would use AYUMIA?', options: { '1-5': '1 to 5', '6-20': '6 to 20', '21-50': '21 to 50', '50+': 'More than 50' } },
  },
};

const de: DomainMessages = {
  registration: {
    FR: { label: 'SIREN- oder SIRET-Nummer', help: 'Optional. Damit finden wir Ihre Betriebsstätten und Ihre eingetragene Tätigkeit.' },
    BE: { label: 'Unternehmensnummer (ZDU)', help: 'Optional. 10 Ziffern, zum Beispiel 0403.170.701.' },
    CH: { label: 'UID-Nummer', help: 'Optional. Format CHE-123.456.789.' },
    LU: { label: 'RCS-Nummer', help: 'Optional. Zum Beispiel B123456.' },
    OTHER: { label: 'Registernummer des Unternehmens', help: 'Optional. Handelsregister- oder USt-IdNr., wie auf Ihren Dokumenten.' },
  },
  questions: {
    target_domains: {
      label: 'Was möchten Sie mit AYUMIA verwalten?',
      help: 'Mehrfachauswahl möglich. Wir verfeinern das im Gespräch.',
      options: { sales: 'Verkauf', purchasing: 'Einkauf', inventory: 'Lager', production: 'Produktion', quality: 'Qualität', traceability: 'Rückverfolgbarkeit', accounting: 'Rechnungsstellung', integrations: 'E-Commerce und Schnittstellen' },
    },
    current_systems: { label: 'Welche Werkzeuge nutzen Sie heute?', options: { spreadsheet: 'Tabellen (Excel…)', custom: 'Individualsoftware', paper: 'Papier', other: 'Andere' } },
    industry: { label: 'Ihre Branche', options: { food: 'Lebensmittel', manufacturing: 'Industrie', distribution: 'Handel und Vertrieb', textile: 'Textil', other: 'Andere' } },
    site_count_band: { label: 'Wie viele Standorte (Werkstätten, Lager, Läden)?', options: { '1': '1', '2-3': '2 bis 3', '4+': '4 oder mehr' } },
    user_count_band: { label: 'Wie viele Personen würden AYUMIA nutzen?', options: { '1-5': '1 bis 5', '6-20': '6 bis 20', '21-50': '21 bis 50', '50+': 'Mehr als 50' } },
  },
};

const es: DomainMessages = {
  registration: {
    FR: { label: 'Número SIREN o SIRET', help: 'Opcional. Nos permite encontrar sus establecimientos y su actividad registrada.' },
    BE: { label: 'Número de empresa (BCE)', help: 'Opcional. 10 cifras, por ejemplo 0403.170.701.' },
    CH: { label: 'Número IDE', help: 'Opcional. Formato CHE-123.456.789.' },
    LU: { label: 'Número RCS', help: 'Opcional. Por ejemplo B123456.' },
    OTHER: { label: 'Número de registro de la empresa', help: 'Opcional. Número del registro mercantil o NIF/IVA, tal como figura en sus documentos.' },
  },
  questions: {
    target_domains: {
      label: '¿Qué desea gestionar con AYUMIA?',
      help: 'Puede elegir varias opciones. Lo afinaremos durante la entrevista.',
      options: { sales: 'Ventas', purchasing: 'Compras', inventory: 'Inventario', production: 'Producción', quality: 'Calidad', traceability: 'Trazabilidad', accounting: 'Facturación', integrations: 'Comercio electrónico e integraciones' },
    },
    current_systems: { label: '¿Qué herramientas utiliza hoy?', options: { spreadsheet: 'Hojas de cálculo (Excel…)', custom: 'Software a medida', paper: 'Papel', other: 'Otra' } },
    industry: { label: 'Su sector', options: { food: 'Agroalimentario', manufacturing: 'Industria', distribution: 'Comercio y distribución', textile: 'Textil', other: 'Otro' } },
    site_count_band: { label: '¿Cuántos centros (talleres, almacenes, tiendas)?', options: { '1': '1', '2-3': '2 a 3', '4+': '4 o más' } },
    user_count_band: { label: '¿Cuántas personas usarían AYUMIA?', options: { '1-5': '1 a 5', '6-20': '6 a 20', '21-50': '21 a 50', '50+': 'Más de 50' } },
  },
};

const it: DomainMessages = {
  registration: {
    FR: { label: 'Numero SIREN o SIRET', help: 'Facoltativo. Ci permette di trovare le sue sedi e l’attività registrata.' },
    BE: { label: 'Numero d’impresa (BCE)', help: 'Facoltativo. 10 cifre, per esempio 0403.170.701.' },
    CH: { label: 'Numero IDI', help: 'Facoltativo. Formato CHE-123.456.789.' },
    LU: { label: 'Numero RCS', help: 'Facoltativo. Per esempio B123456.' },
    OTHER: { label: 'Numero di registrazione dell’azienda', help: 'Facoltativo. Numero del registro delle imprese o partita IVA, come riportato sui documenti.' },
  },
  questions: {
    target_domains: {
      label: 'Cosa vuole gestire con AYUMIA?',
      help: 'Più scelte possibili. Le affineremo durante il colloquio.',
      options: { sales: 'Vendite', purchasing: 'Acquisti', inventory: 'Magazzino', production: 'Produzione', quality: 'Qualità', traceability: 'Tracciabilità', accounting: 'Fatturazione', integrations: 'E-commerce e integrazioni' },
    },
    current_systems: { label: 'Quali strumenti usa oggi?', options: { spreadsheet: 'Fogli di calcolo (Excel…)', custom: 'Software su misura', paper: 'Carta', other: 'Altro' } },
    industry: { label: 'Il suo settore', options: { food: 'Agroalimentare', manufacturing: 'Industria', distribution: 'Commercio e distribuzione', textile: 'Tessile', other: 'Altro' } },
    site_count_band: { label: 'Quante sedi (laboratori, magazzini, negozi)?', options: { '1': '1', '2-3': 'Da 2 a 3', '4+': '4 o più' } },
    user_count_band: { label: 'Quante persone userebbero AYUMIA?', options: { '1-5': 'Da 1 a 5', '6-20': 'Da 6 a 20', '21-50': 'Da 21 a 50', '50+': 'Più di 50' } },
  },
};

const pt: DomainMessages = {
  registration: {
    FR: { label: 'Número SIREN ou SIRET', help: 'Opcional. Permite-nos encontrar os seus estabelecimentos e a atividade registada.' },
    BE: { label: 'Número de empresa (BCE)', help: 'Opcional. 10 algarismos, por exemplo 0403.170.701.' },
    CH: { label: 'Número IDE', help: 'Opcional. Formato CHE-123.456.789.' },
    LU: { label: 'Número RCS', help: 'Opcional. Por exemplo B123456.' },
    OTHER: { label: 'Número de registo da empresa', help: 'Opcional. Número do registo comercial ou NIF/IVA, tal como consta nos seus documentos.' },
  },
  questions: {
    target_domains: {
      label: 'O que pretende gerir com o AYUMIA?',
      help: 'Várias escolhas possíveis. Vamos afinar durante a entrevista.',
      options: { sales: 'Vendas', purchasing: 'Compras', inventory: 'Stocks', production: 'Produção', quality: 'Qualidade', traceability: 'Rastreabilidade', accounting: 'Faturação', integrations: 'Comércio eletrónico e integrações' },
    },
    current_systems: { label: 'Que ferramentas utiliza hoje?', options: { spreadsheet: 'Folhas de cálculo (Excel…)', custom: 'Software à medida', paper: 'Papel', other: 'Outra' } },
    industry: { label: 'O seu setor', options: { food: 'Agroalimentar', manufacturing: 'Indústria', distribution: 'Comércio e distribuição', textile: 'Têxtil', other: 'Outro' } },
    site_count_band: { label: 'Quantos locais (oficinas, armazéns, lojas)?', options: { '1': '1', '2-3': '2 a 3', '4+': '4 ou mais' } },
    user_count_band: { label: 'Quantas pessoas utilizariam o AYUMIA?', options: { '1-5': '1 a 5', '6-20': '6 a 20', '21-50': '21 a 50', '50+': 'Mais de 50' } },
  },
};

const nl: DomainMessages = {
  registration: {
    FR: { label: 'SIREN- of SIRET-nummer', help: 'Optioneel. Hiermee vinden we uw vestigingen en uw geregistreerde activiteit.' },
    BE: { label: 'Ondernemingsnummer (KBO)', help: 'Optioneel. 10 cijfers, bijvoorbeeld 0403.170.701.' },
    CH: { label: 'UID-nummer', help: 'Optioneel. Formaat CHE-123.456.789.' },
    LU: { label: 'RCS-nummer', help: 'Optioneel. Bijvoorbeeld B123456.' },
    OTHER: { label: 'Registratienummer van het bedrijf', help: 'Optioneel. KvK- of btw-nummer, zoals op uw documenten.' },
  },
  questions: {
    target_domains: {
      label: 'Wat wilt u met AYUMIA beheren?',
      help: 'Meerdere keuzes mogelijk. We verfijnen dit tijdens het gesprek.',
      options: { sales: 'Verkoop', purchasing: 'Inkoop', inventory: 'Voorraad', production: 'Productie', quality: 'Kwaliteit', traceability: 'Traceerbaarheid', accounting: 'Facturatie', integrations: 'E-commerce en koppelingen' },
    },
    current_systems: { label: 'Welke tools gebruikt u vandaag?', options: { spreadsheet: 'Spreadsheets (Excel…)', custom: 'Maatwerksoftware', paper: 'Papier', other: 'Andere' } },
    industry: { label: 'Uw sector', options: { food: 'Voeding', manufacturing: 'Industrie', distribution: 'Groothandel en distributie', textile: 'Textiel', other: 'Andere' } },
    site_count_band: { label: 'Hoeveel locaties (werkplaatsen, magazijnen, winkels)?', options: { '1': '1', '2-3': '2 tot 3', '4+': '4 of meer' } },
    user_count_band: { label: 'Hoeveel mensen zouden AYUMIA gebruiken?', options: { '1-5': '1 tot 5', '6-20': '6 tot 20', '21-50': '21 tot 50', '50+': 'Meer dan 50' } },
  },
};

const ar: DomainMessages = {
  registration: {
    FR: { label: 'رقم SIREN أو SIRET', help: 'اختياري. يتيح لنا العثور على مؤسساتكم ونشاطكم المسجَّل.' },
    BE: { label: 'رقم المؤسسة (BCE)', help: 'اختياري. 10 أرقام، مثلًا 0403.170.701.' },
    CH: { label: 'رقم IDE', help: 'اختياري. بالصيغة CHE-123.456.789.' },
    LU: { label: 'رقم RCS', help: 'اختياري. مثلًا B123456.' },
    OTHER: { label: 'رقم تسجيل الشركة', help: 'اختياري. رقم السجل التجاري أو رقم التعريف الضريبي، كما يظهر في وثائقكم.' },
  },
  questions: {
    target_domains: {
      label: 'ما الذي تريدون إدارته باستخدام AYUMIA؟',
      help: 'يمكن اختيار أكثر من خيار. سندقّق ذلك خلال المقابلة.',
      options: { sales: 'المبيعات', purchasing: 'المشتريات', inventory: 'المخزون', production: 'الإنتاج', quality: 'الجودة', traceability: 'التتبّع', accounting: 'الفوترة', integrations: 'التجارة الإلكترونية والربط' },
    },
    current_systems: { label: 'ما الأدوات التي تستخدمونها اليوم؟', options: { spreadsheet: 'جداول بيانات (Excel…)', custom: 'برنامج مُصمَّم خصيصًا', paper: 'ورق', other: 'أخرى' } },
    industry: { label: 'قطاعكم', options: { food: 'الصناعات الغذائية', manufacturing: 'الصناعة', distribution: 'التجارة والتوزيع', textile: 'النسيج', other: 'آخر' } },
    site_count_band: { label: 'كم عدد المواقع (ورشات، مستودعات، متاجر)؟', options: { '1': '1', '2-3': 'من 2 إلى 3', '4+': '4 أو أكثر' } },
    user_count_band: { label: 'كم شخصًا سيستخدم AYUMIA؟', options: { '1-5': 'من 1 إلى 5', '6-20': 'من 6 إلى 20', '21-50': 'من 21 إلى 50', '50+': 'أكثر من 50' } },
  },
};

export const DOMAIN_MESSAGES: Record<Locale, DomainMessages> = { fr, en, de, es, it, pt, nl, ar };
